import { useCustomMutation } from '@refinedev/core';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CopyButton } from '@/components/copy-button';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api-client';
import type { RevealCodeResponse } from '@/lib/types';

const AUTO_CLEAR_SEC = 90;

interface Props {
  accountId: string;
  codeIndex: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RevealCodeDialog({ accountId, codeIndex, open, onOpenChange }: Props) {
  const { mutate, isLoading } = useCustomMutation<RevealCodeResponse>();
  const [reason, setReason] = useState('');
  const [revealed, setRevealed] = useState<RevealCodeResponse['data'] | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(AUTO_CLEAR_SEC);

  useEffect(() => {
    if (!revealed) return;
    setSecondsLeft(AUTO_CLEAR_SEC);
    const interval = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          setRevealed(null);
          setShowSecret(false);
          toast.info('Code ocultado automaticamente.');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [revealed]);

  useEffect(() => {
    if (!open) {
      setReason('');
      setRevealed(null);
      setShowSecret(false);
      setSecondsLeft(AUTO_CLEAR_SEC);
    }
  }, [open]);

  const onReveal = () => {
    if (codeIndex === null) return;
    if (reason.trim().length < 10) {
      toast.error('Motivo precisa ter pelo menos 10 caracteres.');
      return;
    }
    mutate(
      {
        url: `/psn-accounts/${accountId}/recovery-codes/${codeIndex}/reveal`,
        method: 'post',
        values: { reason: reason.trim() },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (result) => {
          setRevealed((result.data as unknown as RevealCodeResponse).data);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.errorCode === 'rate_limited') {
            const retry = (err.body as { retry_after_sec?: number })?.retry_after_sec ?? 0;
            const min = Math.ceil(retry / 60);
            toast.error(`Limite atingido. Tente novamente em ~${min} min.`);
          } else {
            toast.error('Falha ao revelar code.');
          }
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revelar recovery code #{codeIndex}</DialogTitle>
          <DialogDescription>
            Audit log central + automation log gravam essa ação.
          </DialogDescription>
        </DialogHeader>

        {!revealed ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Motivo (mín. 10 chars) *</Label>
              <Textarea
                id="reason"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex: entregando code 5 ao cliente fulano do pedido #4321"
              />
              <p className="text-xs text-muted-foreground">{reason.trim().length} / 10</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={onReveal} disabled={isLoading || reason.trim().length < 10}>
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Revelar
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2 text-sm">
              <span className="text-muted-foreground">
                Limpa em <span className="font-mono text-foreground">{secondsLeft}s</span>
              </span>
              <Button size="sm" variant="ghost" onClick={() => setShowSecret((s) => !s)}>
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {showSecret ? 'Ocultar' : 'Mostrar'}
              </Button>
            </div>

            <div className="space-y-1">
              <Label className="text-xs uppercase text-muted-foreground">Code #{revealed.code_index}</Label>
              <div className="flex items-center gap-1 rounded-md border bg-background px-3 py-2">
                <span className="flex-1 truncate font-mono text-sm">
                  {showSecret ? revealed.code : '•'.repeat(Math.min((revealed.code ?? '').length, 16))}
                </span>
                {showSecret && revealed.code && <CopyButton value={revealed.code} label="Code" />}
              </div>
            </div>

            {revealed.assigned_reference && (
              <div className="space-y-1">
                <Label className="text-xs uppercase text-muted-foreground">Atribuído a</Label>
                <p className="text-sm">{revealed.assigned_reference}</p>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Restantes nesta hora: {revealed.rate_limit_remaining}
            </p>

            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
